import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { applyLanguage } from './domTranslator';
import { loadDictionary } from './dictionaryLoader';

/**
 * Lightweight i18n provider — manages active language state only.
 *
 * Translation happens via:
 *   • <T>English text</T>     — see src/i18n/T.js
 *   • useT() hook             — for translating in JS (aria-label etc.)
 *   • Dictionary lookup       — see src/i18n/dictionary.js
 *
 * Previous Google Translate Widget integration has been removed. The widget
 * was unreliable in production (race conditions with React, browser
 * extension interference, inconsistent cookie behaviour). The owner asked
 * for hand-curated translations instead — those live in dictionary.js with
 * full coverage for the 14 supported languages.
 *
 * Flag rendering: we use `flagCode` (ISO-3166 country code, lowercase) to
 * pull SVG flags from flagcdn.com — emoji flags don't render as flags on
 * Windows (they fall back to letter pairs like "US"/"ES").
 */

export const AVAILABLE_LANGUAGES = [
  { code: 'en', label: 'English',    nativeLabel: 'English',    flagCode: 'us' },
  { code: 'es', label: 'Spanish',    nativeLabel: 'Español',    flagCode: 'es' },
  { code: 'fr', label: 'French',     nativeLabel: 'Français',   flagCode: 'fr' },
  { code: 'de', label: 'German',     nativeLabel: 'Deutsch',    flagCode: 'de' },
  { code: 'it', label: 'Italian',    nativeLabel: 'Italiano',   flagCode: 'it' },
  { code: 'pt', label: 'Portuguese', nativeLabel: 'Português',  flagCode: 'br' },
  { code: 'nl', label: 'Dutch',      nativeLabel: 'Nederlands', flagCode: 'nl' },
  { code: 'ru', label: 'Russian',    nativeLabel: 'Русский',    flagCode: 'ru' },
  { code: 'tr', label: 'Turkish',    nativeLabel: 'Türkçe',     flagCode: 'tr' },
  { code: 'ar', label: 'Arabic',     nativeLabel: 'العربية',    flagCode: 'sa' },
  { code: 'zh', label: 'Chinese',    nativeLabel: '中文',        flagCode: 'cn' },
  { code: 'ja', label: 'Japanese',   nativeLabel: '日本語',      flagCode: 'jp' },
  { code: 'ko', label: 'Korean',     nativeLabel: '한국어',      flagCode: 'kr' },
  { code: 'hi', label: 'Hindi',      nativeLabel: 'हिन्दी',      flagCode: 'in' },
];

const VALID_LANG_CODES = new Set(AVAILABLE_LANGUAGES.map((l) => l.code));

const I18nContext = createContext({
  lang: 'en',
  setLang: () => {},
});

const STORAGE_KEY = 'nyc_lang';

function detectInitialLanguage() {
  if (typeof window === 'undefined') return 'en';
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && VALID_LANG_CODES.has(saved)) return saved;
  } catch {}
  try {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get('lang');
    if (fromUrl && VALID_LANG_CODES.has(fromUrl)) {
      try { localStorage.setItem(STORAGE_KEY, fromUrl); } catch {}
      return fromUrl;
    }
  } catch {}
  // Always open in English for first-time visitors. We deliberately do NOT
  // auto-detect navigator.language — the owner wants the site to ALWAYS load
  // in English by default (a Turkish browser was making it open in Turkish).
  // A returning visitor's explicit choice (localStorage, checked above) or a
  // ?lang= URL param still take precedence.
  return 'en';
}

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => detectInitialLanguage());
  // Bumped once the lazily-loaded dictionary chunk lands. Nothing reads the
  // number — it exists to re-render this provider, which hands every
  // useI18n() consumer a fresh context value so <T> nodes that already
  // rendered in English translate themselves on the second pass.
  const [dictVersion, setDictVersion] = useState(0);
  // Latest language the user asked for. The dictionary import is async, so
  // a fast es → en switch could otherwise let the resolved es callback
  // re-translate a page the user just put back into English.
  const langRef = useRef(lang);

  /**
   * Load the dictionary chunk if needed, then translate the live DOM and
   * re-render <T> consumers. English needs no chunk — it IS the source text.
   */
  const activateLanguage = useCallback((newLang) => {
    langRef.current = newLang;
    if (newLang === 'en') {
      applyLanguage('en');
      return;
    }
    loadDictionary().then(() => {
      if (langRef.current !== newLang) return; // user switched again mid-load
      setDictVersion((v) => v + 1);
      // Drive the site-wide DOM translator — walks every text node and
      // swaps in dictionary translations. Handles ALL components without
      // each one needing to be wired with <T>. The translator also installs
      // a MutationObserver so React re-renders + SPA navigation stay
      // translated.
      applyLanguage(newLang);
    });
  }, []);

  const setLang = useCallback((newLang) => {
    if (!VALID_LANG_CODES.has(newLang)) return;
    setLangState(newLang);
    try { localStorage.setItem(STORAGE_KEY, newLang); } catch {}
    if (typeof document !== 'undefined') {
      document.documentElement.lang = newLang;
      // RTL languages need direction flip — Arabic + Hebrew (we don't have
      // Hebrew yet but room to add). Persian (fa) + Urdu (ur) would also
      // go here. Setting on <html> lets all child elements inherit.
      document.documentElement.dir = newLang === 'ar' ? 'rtl' : 'ltr';
    }
    activateLanguage(newLang);
  }, [activateLanguage]);

  // Keep <html lang> + dir in sync on first mount + reload, AND kick off
  // the DOM translator for returning visitors whose saved choice is non-EN.
  // Defer the first walk by one tick so React has time to mount the page
  // (the translator scans the live DOM, not the React virtual tree).
  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }
    if (lang !== 'en') {
      // Wait for React to finish initial mount + first render of nested
      // routes. A small timeout is more reliable than requestIdleCallback
      // here because we want the walk to happen AFTER paint.
      const t = setTimeout(() => activateLanguage(lang), 100);
      return () => clearTimeout(t);
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Legacy `t(key, vars)` shim — kept so any older callers that still
   * use `useI18n().t(...)` don't break. New code should use <T> from
   * src/i18n/T.js instead. This shim just returns the key (assumes the
   * "key" is the English text and we have no manual translations for
   * old-style namespaced keys).
   */
  const t = useCallback(
    (key, vars = null) => {
      let str = key;
      if (vars && typeof str === 'string') {
        str = str.replace(/\{\{(\w+)\}\}/g, (_, name) =>
          vars[name] != null ? String(vars[name]) : `{{${name}}}`
        );
      }
      return str;
    },
    []
  );

  // dictVersion is in the value on purpose: it changes the object identity
  // when the dictionary chunk arrives, which is what re-renders text that
  // already mounted in English. Do not drop it if this ever gets memoised.
  return (
    <I18nContext.Provider value={{ lang, setLang, t, dictVersion }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = () => useContext(I18nContext);
